import { useTranslation } from "react-i18next";
import { ChevronDown } from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import DropdownMenuListItem from "@/components/controls/OptionsDropdownMenu/DropdownMenuListItem";

export interface ShowAsOption<TMode extends string> {
  mode: TMode;
  icon: JSX.Element;
  labelKey: string;
  disabled?: boolean;
  disabledTooltipKey?: string;
}

interface ShowAsDropdownProps<TMode extends string> {
  selectedMode: TMode;
  options: ShowAsOption<TMode>[];
  onSelect: (mode: TMode) => void;
  dataTest?: string;
  /** The caption before the selection; "Show as" unless the caller names one. */
  labelKey?: string;
}

const ShowAsDropdown = <TMode extends string>({
  selectedMode,
  options,
  onSelect,
  dataTest,
  labelKey = "chat.showAs"
}: ShowAsDropdownProps<TMode>) => {
  const { t } = useTranslation();
  const selected = options.find((o) => o.mode === selectedMode);

  if (!selected) return null;

  return (
    <div className="inline-flex items-center rounded-md border-[0.5px] border-border-badge-default bg-badges-default">
      <div className="h-7 px-[6px] flex items-center">
        <Text variant="body" className="text-secondary text-nowrap">
          {t(labelKey)}
        </Text>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="group inline-flex items-center gap-[6px] h-7 px-[6px] rounded-r-md border-l-[0.5px] border-border-badge-default hover:bg-surface-hover-strong transition-colors duration-200 cursor-pointer"
            data-test={dataTest}
          >
            <IconContainer size="sm" color="secondary">
              {selected.icon}
            </IconContainer>
            <Text variant="body" className="text-nowrap">
              {t(selected.labelKey)}
            </Text>
            <IconContainer
              size="5"
              color="secondary"
              className="transition-transform duration-200 group-data-[state=open]:rotate-180 group-data-[state=closed]:rotate-0"
            >
              <ChevronDown />
            </IconContainer>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {options.map((option) => (
            <DropdownMenuListItem
              key={option.mode}
              option={option.mode}
              text={t(option.labelKey)}
              customIcon={
                <IconContainer size="sm" color="secondary">
                  {option.icon}
                </IconContainer>
              }
              onSelect={() => onSelect(option.mode)}
              selected={selectedMode === option.mode}
              disabled={option.disabled}
              disabledTooltipText={
                option.disabledTooltipKey
                  ? t(option.disabledTooltipKey)
                  : undefined
              }
            />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default ShowAsDropdown;
